import { css } from "@emotion/css";
import { Button, Typography, Tooltip, Box, Icons } from "@analyticsMD/quil";

import { FC, useContext } from "react";
import moment from "moment/moment";
import APIContext from "../contexts/APIContext";
import PatientDisposition from "../types/PatientDisposition";
import EDD from "../types/EDD";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { deepPurple, green, grey, red } from "@mui/material/colors";

const styles = {
  suggestionContainer: css`
    display: flex;
    align-items: center;
    background-color: ${deepPurple[50]};
    padding: 0rem 0.75rem;
    border-radius: 0.4rem;
    border: 1px solid ${deepPurple[100]};
    gap: 0.5rem;
    min-height: 2.5rem;
  `,
  suggestionContent: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    font-size: 0.75rem;
    font-weight: 500;
  `,
  suggestionText: css`
    color: ${grey[900]};
    font-weight: 400;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 145px;
  `,
  suggestionTextWithTooltip: css`
    color: ${grey[900]};
    font-weight: 400;
    cursor: pointer;
    border-bottom: 1px dashed ${grey[900]};
    text-decoration: none;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 145px;
  `,
  actionButtons: css`
    display: flex;
    gap: 0.8rem;
    align-items: center;
  `,
  tooltipContent: css`
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0.25rem;
  `,
  tooltipHeader: css`
    font-size: 0.875rem;
    font-weight: bold;
    width: 100%;
    margin-bottom: 0.5rem;
  `,
  quotesList: css`
    margin: 0;
    padding: 0;
  `,
  quotesListItem: css`
    font-size: 0.875rem;
    &:not(:last-child) {
      margin-bottom: 0.4rem;
    }
  `,
  tooltipFooter: css`
    font-style: italic;
    font-size: 0.75rem;
    font-weight: 100;
    margin-top: 0.7rem;
    width: 100%;
  `,
  dispositionText: css`
    align-self: flex-start;
  `,
};

const sxStyles = {
  actionButton: {
    borderRadius: "50%",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "auto",
  },
  acceptButton: {
    color: green[400],
    "&:focus": {
      outline: `2px solid ${green[400]}`,
      outlineOffset: "2px",
    },
    svg: {
      fontSize: "0.75rem",
    },
  },
  rejectButton: {
    color: red[600],
    "&:focus": {
      outline: `2px solid ${red[600]}`,
      outlineOffset: "2px",
    },
    svg: {
      fontSize: "0.75rem",
    },
  },
  tooltipContainer: {
    marginLeft: "7rem !important",
  },
  tooltip: {
    display: "flex",
    justifyContent: "center",
  },
  tooltipItems: {
    display: "flex",
    flexDirection: "column",
    width: "-webkit-fill-available",
    marginLeft: "1rem",
  },
  dispositionTooltip: {
    "&.MuiTooltip-tooltip": {
      width: "auto",
    },
  },
};

interface SuggestionConfirmationProps {
  suggestionType: string;
  suggestion: PatientDisposition | EDD;
  updateFn?: (...args: any[]) => void;
  disabled?: boolean;
}

const dispositionTooltipContent = (suggestion: PatientDisposition) => {
  const features = suggestion.suggestion_context?.quotes?.filter(
    (q) => q.type === "feature"
  );
  const hasFeatures = features && features.length > 0;

  const notes = suggestion.suggestion_context?.quotes?.filter(
    (q) => q.type !== "feature"
  );
  const hasNotes = notes && notes.length > 0;

  const predictionSection = () => {
    const probability = suggestion.suggestion_context?.probability;
    const percentage = probability
      ? `${(probability * 100).toFixed(1)}%`
      : "N/A";
    return (
      <>
        <Box sx={{ marginBottom: 1.5 }} className={styles.tooltipHeader}>
          Suggested Disposition
          <Box fontWeight={500} marginTop={1}>
            {suggestion.disposition?.display_name}
          </Box>
        </Box>
        <Box sx={{ marginBottom: 1.5 }} className={styles.tooltipHeader}>
          Model Prediction Probability
          <Box fontWeight={500} marginTop={1}>
            {percentage}
          </Box>
        </Box>
      </>
    );
  };
  const relevantNotesSection = () => {
    const formatQuote = (quote) => {
      const dateFormatted = moment(quote.time).format("MM/DD");
      return (
        <Typography>
          {`"${quote.quote}" - `}
          <i>{`(${quote.author}, ${dateFormatted})`}</i>
        </Typography>
      );
    };
    return (
      <>
        <Box className={styles.tooltipHeader}>Relevant Therapy Notes</Box>
        <Box sx={sxStyles.tooltipItems}>
          <ul className={styles.quotesList}>
            {notes.map((quote, index) => (
              <li key={index} className={styles.quotesListItem}>
                {formatQuote(quote)}
              </li>
            ))}
          </ul>
        </Box>
      </>
    );
  };

  const keyFeaturesSection = () => (
    <>
      <Box className={styles.tooltipHeader}>Key Features & Insights</Box>
      <Box sx={sxStyles.tooltipItems}>
        <ul className={styles.quotesList}>
          {features.map((quote, index) => (
            <li key={index} className={styles.quotesListItem}>
              {quote.quote}
            </li>
          ))}
        </ul>
      </Box>
    </>
  );

  const disclaimerSection = () => (
    <>
      <Box className={styles.tooltipFooter}>
        <span>* Key features and insights from 250+ features</span>
      </Box>
    </>
  );

  return (
    <div className={styles.tooltipContent}>
      {predictionSection()}
      {hasFeatures && keyFeaturesSection()}
      {hasNotes && relevantNotesSection()}
      {hasFeatures && disclaimerSection()}
    </div>
  );
};

const eddTooltipContent = (suggestion: EDD) => {
  const roundedProbability =
    Math.round(suggestion.suggestion_context?.probability * 10) / 10; // round to one decimal place
  return (
    <Box className={styles.tooltipContent}>
      <Box sx={{ marginBottom: 1.5 }} className={styles.tooltipHeader}>
        Model Prediction Probability
        <Box fontWeight={500} marginTop={1}>
          {roundedProbability}%
        </Box>
      </Box>
      <Box className={styles.tooltipHeader}>Key Features & Insights</Box>
      <Box sx={sxStyles.tooltipItems}>
        <ul className={styles.quotesList}>
          {suggestion.suggestion_context?.quotes?.map((quote, index) => (
            <li key={index} className={styles.quotesListItem}>
              {quote.quote}
            </li>
          ))}
        </ul>
      </Box>
      <Box className={styles.tooltipFooter}>
        <span>* Key features and insights from 500+ features</span>
      </Box>
    </Box>
  );
};

const tooltipContent = (
  suggestion: PatientDisposition | EDD,
  suggestionType: string
) => {
  if (suggestionType === "disposition") {
    return dispositionTooltipContent(suggestion as PatientDisposition);
  }
  return eddTooltipContent(suggestion as EDD);
};

const SuggestionConfirmation: FC<SuggestionConfirmationProps> = ({
  suggestionType,
  suggestion,
  updateFn,
  disabled,
}) => {
  let typedSuggestion;
  let suggestionValue;
  if (suggestionType !== "disposition") {
    typedSuggestion = suggestion as EDD;
    suggestionValue = moment(typedSuggestion.edd_value).format(
      "ddd MM/DD/YYYY"
    );
  } else {
    typedSuggestion = suggestion as PatientDisposition;
    suggestionValue = typedSuggestion.disposition.display_name;
  }

  const { configurationQuery } = useContext(APIContext);
  const isSuggestionExplainabilityEnabled =
    configurationQuery?.data?.[0]?.suggestion_explainability_enabled ?? true;

  const suggestionContext = suggestion?.suggestion_context;

  const suggestionTextElement = (
    <span
      className={
        suggestionContext && isSuggestionExplainabilityEnabled
          ? styles.suggestionTextWithTooltip
          : styles.suggestionText
      }
    >
      {suggestionValue}
    </span>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <div className={styles.suggestionContainer}>
        <Icons.QBadgeIcon />
        <div className={styles.suggestionContent}>
          {suggestionContext && isSuggestionExplainabilityEnabled ? (
            <Tooltip
              title={tooltipContent(typedSuggestion, suggestionType)}
              placement="right"
              slotProps={{
                tooltip: {
                  sx: sxStyles.tooltipContainer,
                },
              }}
              PopperProps={{ sx: sxStyles.tooltip }}
            >
              {suggestionTextElement}
            </Tooltip>
          ) : (
            <Tooltip
              title={suggestionValue}
              placement="right"
              slotProps={{
                tooltip: {
                  sx: {
                    ...sxStyles.dispositionTooltip,
                    ...sxStyles.tooltipContainer,
                  },
                },
              }}
            >
              {suggestionTextElement}
            </Tooltip>
          )}
        </div>
        {!disabled && (
          <div className={styles.actionButtons}>
            <Button
              sx={{ ...sxStyles.actionButton, ...sxStyles.acceptButton }}
              onClick={() => updateFn("ACCEPTED", suggestion)}
              aria-label="Accept suggestion"
            >
              <CheckIcon
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  padding: "0.125rem",
                }}
              />
            </Button>
            <Button
              sx={{ ...sxStyles.actionButton, ...sxStyles.rejectButton }}
              onClick={() => updateFn("REJECTED", suggestion)}
              aria-label="Reject suggestion"
            >
              <CloseIcon
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  padding: "0.125rem",
                }}
              />
            </Button>
          </div>
        )}
      </div>
    </Box>
  );
};

export default SuggestionConfirmation;
